import { memo } from "react";
import { AuditLogChangeType } from "@common/types/auditLog";
import {
    TransactionRuleCondition,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { TransactionRuleConditions } from "@components/transactionRules/TransactionRuleConditions";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    FieldDiffItem,
    RuleDetailsRowData,
    RuleItemSegment,
} from "../utils/historyRuleDetailsRows";
import styles from "../HistoryRuleDetails.module.scss";

const TextLines: React.FC<{ lines: string[]; className?: string }> = ({
    lines,
    className,
}) => (
    <>
        {lines.map((line, i) => (
            <div key={i} className={className ?? styles.textLine}>
                {line}
            </div>
        ))}
    </>
);

function extractRawIds(value: unknown): unknown {
    if (Array.isArray(value)) {
        const hasIdName = value.some(
            (item) => item && typeof item === "object" && "id" in item,
        );
        if (hasIdName) {
            return value.map((item) =>
                item && typeof item === "object" && "id" in item
                    ? (item as { id: unknown }).id
                    : item,
            );
        }
        return value;
    }
    if (value && typeof value === "object" && "id" in value) {
        return (value as { id: unknown }).id;
    }
    return value;
}

function snapshotToCondition(
    snapshot: unknown,
    index: number,
): TransactionRuleCondition | null {
    if (!snapshot || typeof snapshot !== "object") {
        return null;
    }
    const obj = snapshot as Record<string, unknown>;
    if (!("conditionType" in obj)) {
        return null;
    }
    return {
        id: `snap-cond-${index}`,
        conditionType: obj.conditionType,
        value: extractRawIds(obj.value),
        transferDirection: (obj.transferDirection as string) ?? null,
    } as TransactionRuleCondition;
}

function renderSnapshotCondition(
    snapshot: unknown,
    index: number,
    params: {
        row: RuleDetailsRowData;
        transferDirection?: TransferRuleDirection;
    },
): React.ReactNode {
    const condition = snapshotToCondition(snapshot, index);
    if (!condition) {
        return null;
    }
    const { row, transferDirection } = params;
    return (
        <TransactionRuleConditions
            rule={{ id: row.ruleId, conditions: [condition] }}
            counterparties={row.counterparties}
            normalizedDescriptions={row.normalizedDescriptions}
            transferDirection={transferDirection}
        />
    );
}

const ConditionFieldDiffItems: React.FC<{
    items: FieldDiffItem[];
    row: RuleDetailsRowData;
    transferDirection?: TransferRuleDirection;
}> = ({ items, row, transferDirection }) => (
    <>
        {items.map((item, i) => {
            const key = `${row.versionId}-cfd-${i}`;
            if (item.kind === "stable") {
                return (
                    <div key={key}>
                        {renderSnapshotCondition(item.after ?? item.before, i, {
                            row,
                            transferDirection,
                        })}
                    </div>
                );
            }
            if (item.kind === "added") {
                return (
                    <div key={key} className={styles.addedItem}>
                        {renderSnapshotCondition(item.after, i, {
                            row,
                            transferDirection,
                        })}
                    </div>
                );
            }
            if (item.kind === "removed") {
                return (
                    <div key={key} className={styles.removed}>
                        {renderSnapshotCondition(item.before, i, {
                            row,
                            transferDirection,
                        })}
                    </div>
                );
            }
            return (
                <div key={key} className={styles.diffPair}>
                    <div className={styles.removed}>
                        {renderSnapshotCondition(item.before, i, {
                            row,
                            transferDirection,
                        })}
                    </div>
                    <div className={styles.addedItem}>
                        {renderSnapshotCondition(item.after, i, {
                            row,
                            transferDirection,
                        })}
                    </div>
                </div>
            );
        })}
    </>
);

function renderConditionSegment(
    row: RuleDetailsRowData,
    segment: RuleItemSegment<TransactionRuleCondition>,
    params: {
        index: number;
        transferDirection?: TransferRuleDirection;
    },
): React.ReactNode {
    const { index, transferDirection } = params;
    const baseKey = `${row.versionId}-cond-${index}`;
    if (segment.kind === "stable") {
        return (
            <TransactionRuleConditions
                key={baseKey}
                rule={{ id: row.ruleId, conditions: segment.items }}
                counterparties={row.counterparties}
                normalizedDescriptions={row.normalizedDescriptions}
                transferDirection={transferDirection}
            />
        );
    }
    if (segment.kind === "changed") {
        return (
            <div key={baseKey} className={styles.diffPair}>
                <div className={styles.removed}>
                    <TransactionRuleConditions
                        rule={{ id: row.ruleId, conditions: [segment.before] }}
                        counterparties={row.counterparties}
                        normalizedDescriptions={row.normalizedDescriptions}
                        transferDirection={transferDirection}
                    />
                </div>
                <TransactionRuleConditions
                    rule={{ id: row.ruleId, conditions: [segment.after] }}
                    counterparties={row.counterparties}
                    normalizedDescriptions={row.normalizedDescriptions}
                    transferDirection={transferDirection}
                />
            </div>
        );
    }
    if (segment.kind === "added") {
        return (
            <TransactionRuleConditions
                key={baseKey}
                rule={{ id: row.ruleId, conditions: segment.items }}
                counterparties={row.counterparties}
                normalizedDescriptions={row.normalizedDescriptions}
                transferDirection={transferDirection}
            />
        );
    }
    return (
        <div key={baseKey} className={styles.removed}>
            <TransactionRuleConditions
                rule={{ id: row.ruleId, conditions: segment.items }}
                counterparties={row.counterparties}
                normalizedDescriptions={row.normalizedDescriptions}
                transferDirection={transferDirection}
            />
        </div>
    );
}

interface RuleDetailsConditionsCellProps {
    row: { original: RuleDetailsRowData };
    transferDirection?: TransferRuleDirection;
    markerClassName?: string;
}

export const RuleDetailsConditionsCell = memo(
    ({
        row,
        transferDirection,
        markerClassName,
    }: RuleDetailsConditionsCellProps) => {
        const data = row.original;
        const isUpdate = data.changeType === AuditLogChangeType.UPDATE;
        const filteredConditions = transferDirection
            ? data.conditions.filter(
                  (condition) =>
                      condition.transferDirection === transferDirection,
              )
            : data.conditions;
        const filteredPrevConditions = transferDirection
            ? data.prevConditions.filter(
                  (condition) =>
                      condition.transferDirection === transferDirection,
              )
            : data.prevConditions;

        if (data.conditionFieldDiffs.length > 0) {
            return (
                <ViewTableCell
                    className={`${styles.td} ${markerClassName ?? ""}`.trim()}
                >
                    <ConditionFieldDiffItems
                        items={data.conditionFieldDiffs}
                        row={data}
                        transferDirection={transferDirection}
                    />
                </ViewTableCell>
            );
        }

        if (data.conditionSegments !== null) {
            return (
                <ViewTableCell
                    className={`${styles.td} ${markerClassName ?? ""}`.trim()}
                >
                    <div className={styles.segmentStack}>
                        {data.conditionSegments.map((seg, i) =>
                            renderConditionSegment(data, seg, {
                                index: i,
                                transferDirection,
                            }),
                        )}
                    </div>
                </ViewTableCell>
            );
        }

        const hasPrev = isUpdate && filteredPrevConditions.length > 0;
        const hasPrevText = isUpdate && data.prevConditionTexts.length > 0;
        const hasCurrent =
            filteredConditions.length > 0 || data.conditionTexts.length > 0;
        const showDash = isUpdate && !hasPrev && !hasPrevText && !hasCurrent;

        return (
            <ViewTableCell className={markerClassName}>
                {showDash ? (
                    <span className={styles.emptyDash}>-</span>
                ) : (
                    <>
                        {hasPrev && (
                            <div className={styles.removed}>
                                <TransactionRuleConditions
                                    rule={{
                                        id: data.ruleId,
                                        conditions: filteredPrevConditions,
                                    }}
                                    counterparties={data.counterparties}
                                    normalizedDescriptions={
                                        data.normalizedDescriptions
                                    }
                                    transferDirection={transferDirection}
                                />
                            </div>
                        )}
                        {hasPrevText && (
                            <TextLines
                                lines={data.prevConditionTexts}
                                className={styles.removedText}
                            />
                        )}
                        {filteredConditions.length > 0 && (
                            <TransactionRuleConditions
                                rule={{
                                    id: data.ruleId,
                                    conditions: filteredConditions,
                                }}
                                counterparties={data.counterparties}
                                normalizedDescriptions={
                                    data.normalizedDescriptions
                                }
                                transferDirection={transferDirection}
                            />
                        )}
                        {data.conditionTexts.length > 0 && (
                            <TextLines lines={data.conditionTexts} />
                        )}
                    </>
                )}
            </ViewTableCell>
        );
    },
);

RuleDetailsConditionsCell.displayName = "RuleDetailsConditionsCell";
